/**
 * Phase E · Part B — Tomorrow's Absenteeism Risk.
 * Self-contained card: pick a project, see workers predicted at risk of being
 * absent tomorrow (cached 4h; "Refresh" forces a fresh AI run).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { CalendarClock, Loader2, RefreshCw, UserX, Info } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet } from '../lib/api';

const RISK_STYLE = {
  High: 'bg-rose-100 text-rose-800',
  Medium: 'bg-amber-100 text-amber-800',
};

export default function AbsenteeismRiskCard({ initialProjectId = '', className = '' }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(initialProjectId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiGet('/projects').then((r) => {
      const list = (Array.isArray(r) ? r : r?.projects || []).filter((p) => (p.status || 'Active') === 'Active');
      setProjects(list);
      setProjectId((cur) => cur || list[0]?.id || '');
    }).catch(() => {});
  }, []);

  const load = useCallback(async (pid, force = false) => {
    if (!pid) { setData(null); return; }
    setLoading(true);
    try {
      const r = await apiGet(`/ai/projects/${pid}/absenteeism-forecast${force ? '?force=true' : ''}`);
      setData(r);
    } catch (e) {
      if (force) toast.error(e?.response?.data?.detail || 'Could not generate forecast');
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(projectId); }, [projectId, load]);

  const atRisk = data?.forecast || [];

  return (
    <div className={`bg-white border border-slate-200 rounded-lg p-4 ${className}`} data-testid="absenteeism-risk-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="inline-flex items-center gap-2 font-display font-bold text-[#3b4872]">
          <CalendarClock size={17} /> Tomorrow's Absenteeism Risk
        </div>
        {data?.day_of_week && (
          <span className="text-[11px] text-slate-400">{data.day_of_week}, {data.tomorrow_date}{data.cached ? ' · cached' : ''}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1.5 text-xs bg-white max-w-[200px]"
            data-testid="abs-project-picker"
          >
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button
            onClick={() => load(projectId, true)}
            disabled={loading || !projectId}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold rounded-md border border-slate-300 text-slate-600 hover:border-[#3b4872] disabled:opacity-50"
            data-testid="abs-refresh-btn"
            title="Run a fresh AI forecast"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-8 text-center text-sm text-slate-400">Analysing 90 days of attendance…</div>
      ) : atRisk.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500" data-testid="abs-empty">
          <UserX size={24} className="mx-auto mb-2 text-emerald-300" />
          No workers flagged at risk for tomorrow. ✓
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="abs-list">
          <div className="text-[11px] text-slate-500 flex items-center gap-1 mb-1">
            <Info size={11} /> {data.high_count} high-risk of {atRisk.length} flagged · arrange replacements today
          </div>
          <div className="max-h-72 overflow-y-auto space-y-1.5">
            {atRisk.map((a, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-md bg-slate-50 border border-slate-100" data-testid={`abs-worker-${i}`}>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${RISK_STYLE[a.risk_level] || 'bg-slate-200 text-slate-600'}`}>{a.risk_level}</span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{a.worker_name} <span className="text-[10px] text-slate-400 uppercase">{a.worker_type}</span></div>
                  <div className="text-xs text-slate-500 truncate">{a.primary_reason}</div>
                </div>
                {a.confidence && <span className="ml-auto text-[10px] text-slate-400 whitespace-nowrap">{a.confidence} conf.</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
